import React, { useState, useEffect, useCallback } from 'react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import { toastr } from 'react-redux-toastr';
import DownloadExcel from '../../../images/download_excel.svg';
import JklcForecastClone from '../JklcForecastClone/index.jsx';
import { methods, request } from '../../../utils/http-common.js';
import { generateFileName } from '../../../utils/exportUtil.js';
import './demandForecasting.scss';
import { renderSensitiveCellValue } from '../JklcForecastClone/common/sensitiveFields.jsx';

const moduleID = 'demand_forecasting';

const TOP_TABS = [
  'Monthly Forecast Tracker',
  'Final Monthly Forecast',
  'Forecast Model',
  'Analyze Month',
  'Comparison vs Target',
  'Trade Sales (ESF)',
  'Target comparison',
];

const SUB_TABS = [
  'Monthly Forecast Report',
  'MTD Chart',
  'Dealer Segment',
  'Dealer Risk',
  'Account Owner',
  'Account Owner Risk',
  'GYR Risk',
  'Product Category',
  'Insights',
  'WMAPE',
];

// Keys used server-side to identify each tab (adjust to match backend contract)
const TOP_TAB_KEYS = [
  'monthly_forecast_tracker',
  'final_monthly_forecast',
  'forecast_model',
  'analyze_month',
  'comparison_vs_target',
  'trade_sales_esf',
  'target_comparison',
];

const SUB_TAB_KEYS = [
  'monthly_forecast_report',
  'mtd_chart',
  'dealer_segment',
  'dealer_risk',
  'account_owner',
  'account_owner_risk',
  'gyr_risk',
  'product_category',
  'insights',
  'wmape',
];

const TABLE_COLUMN_COUNT = 13;
const SKELETON_ROWS = 14;

const SkeletonBar = ({ variant = 'md' }) => (
  <span className={`skeleton-bar skeleton-bar--${variant}`} />
);

const SkeletonRow = ({ index }) => (
  <tr>
    <td><SkeletonBar variant={index % 3 === 0 ? 'link' : 'sm'} /></td>
    <td><SkeletonBar variant="lg" /></td>
    <td><span className="skeleton-pill" /></td>
    <td><SkeletonBar variant="sm" /></td>
    <td><SkeletonBar variant="md" /></td>
    <td><SkeletonBar variant="md" /></td>
    <td><SkeletonBar variant="sm" /></td>
    <td><SkeletonBar variant="sm" /></td>
    <td><SkeletonBar variant="md" /></td>
    <td><SkeletonBar variant="lg" /></td>
    <td><SkeletonBar variant="md" /></td>
    <td><SkeletonBar variant="md" /></td>
    <td><SkeletonBar variant="md" /></td>
  </tr>
);

const PlaceholderTabContent = () => {
  const [subTab, setSubTab] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState('May-26');

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  // ========================================
  // API CALL
  // ========================================
  const fetchDemandForecastingData = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');

    // const payload = {
    //   module_id: moduleID,
    //   top_tab: TOP_TAB_KEYS[topTab],
    //   top_tab_label: TOP_TABS[topTab],
    //   sub_tab: SUB_TAB_KEYS[subTab],
    //   sub_tab_label: SUB_TABS[subTab],
    //   month: selectedMonth,
    // };

    // const [error, response] = await request(
    //   'redirect/demand-forecasting/fetch-data/',
    //   methods.POST,
    //   payload
    // );

    // if (error) {
    //   console.error('Error fetching demand forecasting data:', error);
    //   setErrorMessage(error.message || 'Failed to fetch demand forecasting data');
    //   setTableData([]);
    //   setLoading(false);
    //   return;
    // }

    // const rows = response?.data?.rows || [];
    // setTableData(rows);
    setLoading(false);
  }, [subTab, selectedMonth]);

  useEffect(() => {
    fetchDemandForecastingData();
  }, [fetchDemandForecastingData]);

  // ========================================
  // EXPORT HANDLER
  // ========================================
  const handleExportExcel = async () => {
    const payload = {
      module_id: moduleID,
      // top_tab: TOP_TAB_KEYS[topTab],
      sub_tab: SUB_TAB_KEYS[subTab],
      month: selectedMonth,
    };

    const [error, response] = await request(
      'redirect/demand-forecasting/export-excel/',
      methods.POST,
      payload,
      { responseType: 'blob' }
    );

    if (error) {
      toastr.error(error.message || 'Download failed');
      return;
    }

    try {
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenamePart = contentDisposition.includes('=')
        ? contentDisposition.split('=').slice(1).join('=')
        : '';
      a.download = `Demand_Forecasting_${generateFileName(filenamePart)}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toastr.error('Failed to process the file');
    }
  };

  // ========================================
  // TAB HANDLERS
  // ========================================
  const handleTopTabChange = (_, value) => {
    // setTopTab(value);
    setSubTab(0); // reset sub tab when top tab changes, adjust if not desired
  };

  const handleSubTabChange = (_, value) => {
    setSubTab(value);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  return (
    <>
      <div className="demand-forecasting__toolbar">
        <FormControl className="demand-forecasting__month-filter" variant="outlined" size="small">
          <InputLabel>Select Month</InputLabel>
          <Select value={selectedMonth} onChange={handleMonthChange} label="Select Month">
            <MenuItem value="May-26">May-26</MenuItem>
            <MenuItem value="Apr-26">Apr-26</MenuItem>
            <MenuItem value="Mar-26">Mar-26</MenuItem>
          </Select>
        </FormControl>

        <Tooltip title="Export to Excel">
          <span className="demand-forecasting__export-btn" onClick={handleExportExcel}>
            <img src={DownloadExcel} alt="Export to Excel" />
          </span>
        </Tooltip>
      </div>

      <Tabs
        className="demand-forecasting__sub-tabs"
        value={subTab}
        onChange={handleSubTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        {SUB_TABS.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {errorMessage && (
        <div style={{ wordWrap: 'break-word', margin: '8px 16px', color: '#d32f2f' }}>
          {errorMessage}
        </div>
      )}

      <div className="demand-forecasting__table-wrap" style={{ marginTop: '20px' }}>
        <table className="demand-forecasting__table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ width: '110px' }}>
                <span className="header-sort">Dealer Code<span className="sort-icon">↕</span></span>
              </th>
              <th rowSpan={2} style={{ width: '170px' }}>
                <span className="header-sort">Dealer Name<span className="sort-icon">↕</span></span>
              </th>
              <th rowSpan={2} style={{ width: '110px' }}>
                <span className="header-sort">Dealer Status<span className="sort-icon">↕</span></span>
              </th>
              <th rowSpan={2} style={{ width: '90px' }}>
                <span className="header-sort">Zone<span className="sort-icon">↕</span></span>
              </th>
              <th rowSpan={2} style={{ width: '90px' }}>
                <span className="header-sort">Region<span className="sort-icon">↕</span></span>
              </th>
              <th rowSpan={2} style={{ width: '100px' }}>
                <span className="header-sort">District<span className="sort-icon">↕</span></span>
              </th>
              <th rowSpan={2} style={{ width: '110px' }}>
                <span className="header-sort">District Code<span className="filter-icon">▼</span></span>
              </th>
              <th rowSpan={2} style={{ width: '90px' }}>
                <span className="header-sort">Tehsil<span className="sort-icon">↕</span></span>
              </th>
              <th rowSpan={2} style={{ width: '120px' }}>
                <span className="header-sort">Dealer Segment<span className="sort-icon">↕</span></span>
              </th>
              <th colSpan={2} className="header-group">EPM</th>
              {/* <th colSpan={2} className="header-group">SAP</th> */}
            </tr>
            <tr>
              <th style={{ width: '130px' }}><span className="header-sort">Account Owner<span className="sort-icon">↕</span></span></th>
              <th style={{ width: '130px' }}><span className="header-sort">Reports To<span className="sort-icon">↕</span></span></th>
              {/* <th style={{ width: '130px' }}><span className="header-sort">Account Owner<span className="sort-icon">↕</span></span></th>
              <th style={{ width: '130px' }}><span className="header-sort">Reports To<span className="sort-icon">↕</span></span></th> */}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <SkeletonRow key={index} index={index} />
              ))
            ) : tableData.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMN_COUNT} style={{ textAlign: 'center', padding: '24px' }}>
                  No Data Available
                </td>
              </tr>
            ) : (
              tableData.map((row, index) => (
                <tr key={row.dealer_code || index}>
                  <td>{row.dealer_code}</td>
                  <td>{renderSensitiveCellValue('dealer_name', row.dealer_name)}</td>
                  <td>{row.dealer_status}</td>
                  <td>{row.zone}</td>
                  <td>{row.region}</td>
                  <td>{row.district}</td>
                  <td>{row.district_code}</td>
                  <td>{row.tehsil}</td>
                  <td>{row.dealer_segment}</td>
                  <td>{renderSensitiveCellValue('epm_account_owner', row.epm_account_owner)}</td>
                  <td>{renderSensitiveCellValue('epm_reports_to', row.epm_reports_to)}</td>
                  {/* <td>{renderSensitiveCellValue('sap_account_owner', row.sap_account_owner)}</td>
                  <td>{renderSensitiveCellValue('sap_reports_to', row.sap_reports_to)}</td> */}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

const DemandForecasting = () => {
  const [topTab, setTopTab] = useState(0);

  return (
    <div className="demand-forecasting">
      <Tabs
        className="demand-forecasting__top-tabs"
        value={topTab}
        onChange={(_, value) => setTopTab(value)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        {TOP_TABS.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {topTab === 0 ? (
        <JklcForecastClone />
      ) : (
        <PlaceholderTabContent />
      )}
    </div>
  );
};

export default DemandForecasting;